import { Button, Flex, Form, Input, InputNumber, Select, Switch } from "antd";
import { useEffect } from "react";
import {
  useCreateSubcategoryMutation,
  useGetCategoriesQuery,
  useUpdateSubcategoryMutation,
} from "../../../../../app/features/admin/admin.api";
import type { Subcategory } from "../../../../../app/features/admin/admin.types";
import CustomModal from "../../../../../components/CustomModal";
import type { ModalType } from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import { slug } from "../../../../../utils/string.types";

interface SubcategoryModalProps {
  open: boolean;
  onCancel: () => void;
  initialValues?: Subcategory | null;
  type?: ModalType["type"];
}

const SubcategoryModal = ({
  open,
  onCancel,
  initialValues,
  type,
}: SubcategoryModalProps) => {
  const [form] = Form.useForm();
  const [createSubcategory, { isLoading: isCreating }] =
    useCreateSubcategoryMutation();
  const [updateSubcategory, { isLoading: isUpdating }] =
    useUpdateSubcategoryMutation();

  const { data: categories, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery({});

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (initialValues && type === "UPDATE") {
      form.setFieldsValue({
        categoryId: initialValues.categoryId,
        name: initialValues.name,
        slug: initialValues.slug,
        sortOrder: initialValues.sortOrder,
        isActive: initialValues.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        sortOrder: 0,
      });
    }
  }, [initialValues, form, open, type]);

  const handleSubmit = async (values: any) => {
    try {
      let res;
      if (initialValues?.id && type === "UPDATE") {
        res = await updateSubcategory({
          id: initialValues.id,
          ...values,
          slug: initialValues?.slug || slug(values.name),
        }).unwrap();
      } else {
        res = await createSubcategory({
          ...values,
          slug: slug(values.name),
        }).unwrap();
      }

      if (res) {
        toast(
          "success",
          type === "UPDATE"
            ? "Subcategory updated successfully"
            : "Subcategory created successfully",
        );
        onCancel();
      }
    } catch (error: any) {
      toast("error", error?.data?.message || "Something went wrong");
    }
  };

  return (
    <CustomModal
      handleClose={onCancel}
      title={type === "UPDATE" ? "Update Subcategory" : "Create Subcategory"}
      open={open}
      width={500}
      footer
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true, sortOrder: 0 }}
        className="custom-form"
        size="large"
      >
        <Form.Item
          label="Category"
          name="categoryId"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select
            placeholder="Select category"
            loading={isCategoriesLoading}
            options={
              Array.isArray(categories)
                ? categories.map((cat) => ({
                    label: cat.name,
                    value: cat.id,
                  }))
                : []
            }
          />
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter subcategory name" }]}
        >
          <Input placeholder="Enter subcategory name" />
        </Form.Item>

        {/* <Form.Item
          label="Slug"
          name="slug"
          rules={[{ required: true, message: "Please enter subcategory slug" }]}
        >
          <Input placeholder="Enter subcategory slug" />
        </Form.Item> */}

        <Form.Item
          label="Sort Order"
          name="sortOrder"
          rules={[{ required: true, message: "Please enter sort order" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter sort order"
          />
        </Form.Item>

        <Form.Item label="Status" name="isActive" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Flex justify="end" gap={8}>
          <Button
            className="min-w-[120px]"
            disabled={isLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="min-w-[120px]"
            htmlType="submit"
            loading={isLoading}
          >
            {type === "UPDATE" ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
};

export default SubcategoryModal;
